/* TreatmentObjectives — full port of the user-provided enhanced HTML mockup.
 *
 *   1. Treatment Objectives — chip grid with icons + selected-count badge.
 *   2. Tooth-by-tooth Movement Plan:
 *      • Legend (Not authored / Fully authored / Partially filled / Selected
 *        + keyboard hint)
 *      • Summary strip — live, with sample teeth + percentage
 *      • Arch progress bars (Upper & Lower) — N/16 + emerald fill
 *      • Tooth chart — 54×62 teeth with FDI number AND tooth type label
 *        (Molar / Premolar / Canine / Lateral / Central), status dot, "✓"
 *        done badge, hover lift, active outline ring, partial-amber state
 *      • Quadrant headers at top + bottom + "↑ Upper · Lower ↓" divider
 *   3. Movement Editor (inline drawer, opens BELOW chart):
 *      • Header — title "Tooth N — <Type>" + sub-line, prev/next arrows,
 *        Close button
 *      • Translation section — 8 direction buttons with Phosphor icons
 *        (Mesial / Distal / Buccal / Lingual / Extrude / Intrude /
 *        Rotate CW / Rotate CCW)
 *      • Metrics — 3-col grid: Amount stepper (mm), Rotation stepper (°),
 *        Torque chips (+ Positive / Signed / − Negative)
 *      • Anchorage dropdown + Stage onset stepper
 *      • Notes textarea + Live preview ("Movement summary preview")
 *      • Footer — Save & Next (advances to next tooth) / Done / Clear tooth
 *      • Keyboard ← / → arrow keys navigate teeth
 *
 * Data shape (backwards-compat with backend ToothMovement):
 *   { tooth, directions[], amount_mm, rotation_deg, torque_deg(via type),
 *     anchorage, notes, movement_type?, magnitude?, start_stage?, end_stage? }
 *
 * The legacy ToothMovementsPicker fields are filled on save so downstream
 * consumers (planner editor, PDFs) keep rendering.
 */
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";
import {
    Check, ArrowLeft, ArrowRight, ArrowUp, ArrowDown,
    ArrowsClockwise, ArrowsCounterClockwise, Ruler, ArrowsLeftRight,
    Tooth as ToothIcon, Trash, Keyboard, Info,
    AlignCenterHorizontal, AlignTop, ArrowsOutLineHorizontal,
    ListNumbers, Smiley, TrendDown, TrendUp,
    ArrowsHorizontal, CaretLeft, CaretRight, XCircle, CheckCircle,
} from "@phosphor-icons/react";

/* ─────────── FDI configuration ─────────── */

const ROW_UR = [18, 17, 16, 15, 14, 13, 12, 11];        // Upper Right
const ROW_UL = [21, 22, 23, 24, 25, 26, 27, 28];        // Upper Left
const ROW_LL = [48, 47, 46, 45, 44, 43, 42, 41];        // Lower (left-side row in display)
const ROW_LR = [31, 32, 33, 34, 35, 36, 37, 38];        // Lower (right-side row in display)

const UPPER_TEETH = [...ROW_UR, ...ROW_UL];
const LOWER_TEETH = [...ROW_LL, ...ROW_LR];
const ALL_TEETH = [...UPPER_TEETH, ...LOWER_TEETH];

const TOOTH_TYPE = (() => {
    const t = {};
    [18, 17, 16, 28, 27, 26, 38, 37, 36, 48, 47, 46].forEach((n) => (t[n] = "Molar"));
    [15, 14, 24, 25, 34, 35, 44, 45].forEach((n) => (t[n] = "Premolar"));
    [13, 23, 33, 43].forEach((n) => (t[n] = "Canine"));
    [12, 22, 32, 42].forEach((n) => (t[n] = "Lateral"));
    [11, 21, 31, 41].forEach((n) => (t[n] = "Central"));
    return t;
})();

/* ─────────── Direction & objective tables ─────────── */

const DIRECTIONS = [
    { id: "Mesial",    label: "Mesial",    legacy: "mesial",       icon: ArrowLeft },
    { id: "Distal",    label: "Distal",    legacy: "distal",       icon: ArrowRight },
    { id: "Buccal",    label: "Buccal",    legacy: "buccal",       icon: ArrowUp },
    { id: "Lingual",   label: "Lingual",   legacy: "lingual",      icon: ArrowDown },
    { id: "Extrude",   label: "Extrude",   legacy: "extrusion",    icon: ArrowUp },
    { id: "Intrude",   label: "Intrude",   legacy: "intrusion",    icon: ArrowDown },
    { id: "RotateCW",  label: "Rotate CW", legacy: "rotation_cw",  icon: ArrowsClockwise },
    { id: "RotateCCW", label: "Rotate CCW",legacy: "rotation_ccw", icon: ArrowsCounterClockwise },
];

const DIR_BY_ID = DIRECTIONS.reduce((acc, d) => ({ ...acc, [d.id]: d }), {});
const LEGACY_TO_DIR = DIRECTIONS.reduce((acc, d) => ({ ...acc, [d.legacy]: d.id }), {});

// Objective key → Phosphor icon (best-effort match to the Tabler set in the mockup).
const OBJECTIVE_ICON = {
    alignment: AlignCenterHorizontal,
    bite_correction: AlignTop,
    midline_correction: ArrowsLeftRight,
    crowding: ListNumbers,
    spacing: ArrowsOutLineHorizontal,
    deep_bite: TrendDown,
    open_bite: TrendUp,
    crossbite: ArrowsLeftRight,
    overjet_reduction: ArrowsHorizontal,
    overbite_reduction: ArrowsHorizontal,
    extraction: Smiley,
    rotation: ArrowsClockwise,
    intrusion: ArrowDown,
    extrusion: ArrowUp,
};

const ANCHORAGE_OPTS = [
    { v: "Absolute", l: "Absolute anchorage" },
    { v: "Maximum",  l: "Maximum anchorage" },
    { v: "Moderate", l: "Moderate anchorage" },
    { v: "Minimum",  l: "Minimum anchorage" },
    { v: "None",     l: "No anchorage needed" },
];

/* ─────────── Helpers ─────────── */

function emptyMovement(tooth) {
    return {
        tooth: String(tooth),
        dirs: [],
        amount: "",
        rotation: "",
        torque: "",       // "positive" / "signed" / "negative"
        anchorage: "",
        stage: "",
        notes: "",
    };
}

/** Hydrate legacy ToothMovement record → UI-shape used by this component */
function hydrate(legacy) {
    if (!legacy) return null;
    const dirsLegacy = legacy.directions || [];
    const dirs = dirsLegacy.map((d) => LEGACY_TO_DIR[d]).filter(Boolean);
    let torque = "";
    if (typeof legacy.torque_deg === "number") {
        if (legacy.torque_deg > 0) torque = "positive";
        else if (legacy.torque_deg < 0) torque = "negative";
        else torque = "signed";
    }
    return {
        tooth: String(legacy.tooth),
        dirs,
        amount: legacy.amount_mm != null ? String(legacy.amount_mm) : "",
        rotation: legacy.rotation_deg != null ? String(legacy.rotation_deg) : "",
        torque,
        anchorage: legacy.anchorage || "",
        stage: legacy.start_stage != null ? String(legacy.start_stage) : "",
        notes: legacy.notes || "",
    };
}

/** Serialise UI-shape → legacy ToothMovement (kept backwards-compat) */
function serialise(m) {
    const dirsLegacy = (m.dirs || []).map((id) => DIR_BY_ID[id]?.legacy).filter(Boolean);
    const out = {
        tooth: String(m.tooth),
        directions: dirsLegacy,
        amount_mm: m.amount === "" ? null : Number(m.amount),
        rotation_deg: m.rotation === "" ? null : Number(m.rotation),
        torque_deg: m.torque === "positive" ? 1 : m.torque === "negative" ? -1 : m.torque === "signed" ? 0 : null,
        anchorage: m.anchorage || "",
        notes: m.notes || "",
        start_stage: m.stage === "" ? null : Number(m.stage),
    };
    // movement_type derived from first direction for the new schema field
    if (dirsLegacy.length) {
        const t = dirsLegacy[0];
        if (t === "rotation_cw" || t === "rotation_ccw") out.movement_type = "rotation";
        else if (t === "intrusion") out.movement_type = "intrusion";
        else if (t === "extrusion") out.movement_type = "extrusion";
        else if (t === "mesial" || t === "distal") out.movement_type = "translation";
        else if (t === "buccal" || t === "lingual") out.movement_type = "torque";
    }
    if (out.amount_mm != null) out.magnitude = out.amount_mm;
    else if (out.rotation_deg != null) out.magnitude = out.rotation_deg;
    return out;
}

/** Author-state for one tooth */
function authorState(m) {
    if (!m) return "empty";
    const dirs = (m.dirs || []).length > 0;
    const amount = !!m.amount;
    if (dirs && amount) return "done";
    if (dirs || m.notes) return "partial";
    return "empty";
}

/** Friendly summary string for the strip and live preview */
function describeMovement(m) {
    const dirs = (m.dirs || []).join(" + ");
    const parts = [];
    if (dirs) parts.push(dirs);
    if (m.amount) parts.push(`${m.amount}mm`);
    if (m.rotation) parts.push(`${m.rotation}° rotation`);
    if (m.torque) parts.push(`${m.torque} torque`);
    if (m.anchorage) parts.push(`${m.anchorage} anchorage`);
    if (m.stage) parts.push(`from stage ${m.stage}`);
    return parts.join(" · ");
}

/* ─────────── Small atoms ─────────── */

function SectionHeader({ title, sub = null, right }) {
    return (
        <div className="flex items-center justify-between mb-1" data-testid={`section-${title.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[15px] font-semibold text-foreground">{title}</span>
                {sub && (
                    <span className="text-[11.5px] italic text-muted-foreground/85">
                        ({sub})
                    </span>
                )}
            </div>
            {right}
        </div>
    );
}

function Stepper({ value, onChange, step = 1, min = 0, max = 999, unit, placeholder, testid, footnote }) {
    const dec = () => {
        const v = parseFloat(value || 0) - step;
        onChange(Math.max(min, Math.round(v * 100) / 100) || "");
    };
    const inc = () => {
        const v = parseFloat(value || 0) + step;
        onChange(Math.min(max, Math.round(v * 100) / 100));
    };
    return (
        <>
            <div className="flex items-center border-[0.5px] border-border rounded-[6px] overflow-hidden bg-card">
                <button
                    type="button"
                    onClick={dec}
                    className="w-8 h-[34px] bg-muted text-muted-foreground hover:bg-emerald-50 hover:text-emerald-700 text-base font-semibold"
                    data-testid={`${testid}-dec`}
                >
                    −
                </button>
                <input
                    type="number"
                    step={step}
                    min={min}
                    max={max}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder || "—"}
                    className="flex-1 h-[34px] border-0 outline-none text-center text-[13px] font-semibold bg-transparent px-1"
                    data-testid={testid}
                />
                <button
                    type="button"
                    onClick={inc}
                    className="w-8 h-[34px] bg-muted text-muted-foreground hover:bg-emerald-50 hover:text-emerald-700 text-base font-semibold"
                    data-testid={`${testid}-inc`}
                >
                    +
                </button>
                {unit && (
                    <span className="text-[11px] text-muted-foreground px-2 border-l-[0.5px] border-border h-[34px] flex items-center bg-muted/40">
                        {unit}
                    </span>
                )}
            </div>
            {footnote && (
                <div className="text-[10px] text-muted-foreground mt-1.5">{footnote}</div>
            )}
        </>
    );
}

/* ─────────── Tooth button ─────────── */

function Tooth({ num, state, active, onClick }) {
    const type = TOOTH_TYPE[num] || "";
    const base =
        "relative w-[54px] h-[62px] rounded-[8px] flex flex-col items-center justify-center gap-[3px] cursor-pointer transition-all select-none shrink-0 border-[1.5px]";
    let cls = "";
    if (active) {
        cls = "bg-card border-emerald-600 -translate-y-[3px] ring-[3px] ring-emerald-600/20";
    } else if (state === "done") {
        cls = "bg-emerald-50 border-emerald-300 hover:border-emerald-600 hover:-translate-y-[2px] hover:shadow-md hover:shadow-emerald-500/15";
    } else if (state === "partial") {
        cls = "bg-amber-50 border-amber-400 hover:border-emerald-600 hover:-translate-y-[2px] hover:shadow-md hover:shadow-emerald-500/15";
    } else {
        cls = "bg-card border-border hover:border-emerald-600 hover:-translate-y-[2px] hover:shadow-md hover:shadow-emerald-500/15";
    }
    const numColor =
        active ? "text-emerald-700"
            : state === "done" ? "text-emerald-900"
            : state === "partial" ? "text-amber-900"
            : "text-muted-foreground";
    const typeColor =
        state === "done" ? "text-emerald-700/70"
            : state === "partial" ? "text-amber-900/70"
            : "text-muted-foreground/70";
    return (
        <button
            type="button"
            onClick={onClick}
            className={`${base} ${cls}`}
            data-testid={`treatment-tooth-${num}`}
            data-state={state}
            data-active={active ? "true" : "false"}
            title={`Tooth ${num} — ${type}`}
        >
            {/* Status dot top-right */}
            {(state === "done" || state === "partial") && (
                <span
                    className={`absolute top-1 right-1.5 w-[7px] h-[7px] rounded-full ${state === "done" ? "bg-emerald-600" : "bg-amber-500"}`}
                />
            )}
            <span className={`text-[13px] font-bold leading-none ${numColor}`}>{num}</span>
            <span className={`text-[8px] uppercase tracking-[0.03em] ${typeColor}`}>{type}</span>
            {/* Done check badge at bottom */}
            {state === "done" && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[7px] font-bold uppercase tracking-[0.03em] px-1 py-[1px] rounded-[3px] bg-emerald-600 text-white">
                    ✓
                </span>
            )}
            {state === "partial" && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[7px] font-bold uppercase tracking-[0.03em] px-1 py-[1px] rounded-[3px] bg-amber-500 text-white">
                    ✓
                </span>
            )}
        </button>
    );
}

/* ─────────── Chart wrapper ─────────── */

function ArchRow({ teeth, byTooth, activeTooth, onTap, testid }) {
    return (
        <div className="flex justify-center gap-1.5 mb-1.5" data-testid={testid}>
            {teeth.map((n) => (
                <Tooth
                    key={n}
                    num={n}
                    state={authorState(byTooth[n])}
                    active={activeTooth === n}
                    onClick={() => onTap(n)}
                />
            ))}
        </div>
    );
}

function ToothChart({ byTooth, activeTooth, onTap }) {
    return (
        <div
            className="bg-muted/40 border-[0.5px] border-border rounded-[12px] p-4 mb-1"
            data-testid="treatment-tooth-chart"
        >
            {/* Upper quadrant labels */}
            <div className="flex justify-between mb-2.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-muted-foreground/70">
                    Upper Right (1.x)
                </span>
                <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-muted-foreground/70">
                    Upper Left (2.x)
                </span>
            </div>
            <ArchRow teeth={ROW_UR} byTooth={byTooth} activeTooth={activeTooth} onTap={onTap} testid="treatment-row-ur" />
            <ArchRow teeth={ROW_UL} byTooth={byTooth} activeTooth={activeTooth} onTap={onTap} testid="treatment-row-ul" />

            {/* Arch divider */}
            <div className="h-px bg-border my-2.5 relative">
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-muted/40 px-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-muted-foreground/70 whitespace-nowrap">
                    ↑ Upper · Lower ↓
                </span>
            </div>

            <ArchRow teeth={ROW_LL} byTooth={byTooth} activeTooth={activeTooth} onTap={onTap} testid="treatment-row-ll" />
            <ArchRow teeth={ROW_LR} byTooth={byTooth} activeTooth={activeTooth} onTap={onTap} testid="treatment-row-lr" />

            {/* Lower quadrant labels */}
            <div className="flex justify-between mt-2.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-muted-foreground/70">
                    Lower Right (4.x)
                </span>
                <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-muted-foreground/70">
                    Lower Left (3.x)
                </span>
            </div>
        </div>
    );
}

/* ─────────── Legend ─────────── */

function Legend() {
    return (
        <div className="flex items-center gap-4 flex-wrap py-2.5 px-3.5 bg-card border-[0.5px] border-border rounded-[8px] mb-4">
            <LegendItem cls="bg-card border-border" label="Not authored" />
            <LegendItem cls="bg-emerald-50 border-emerald-300" label="Fully authored" />
            <LegendItem cls="bg-amber-50 border-amber-400" label="Partially filled" />
            <LegendItem cls="bg-card border-emerald-600 ring-2 ring-emerald-600/25" label="Selected" />
            <div className="ml-auto flex items-center gap-1.5 text-[11px] font-medium text-emerald-700">
                <Keyboard size={13} weight="duotone" />
                <span>← → arrow keys to navigate</span>
            </div>
        </div>
    );
}

function LegendItem({ cls, label }) {
    return (
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <div className={`w-3 h-3 rounded-[3px] border ${cls}`} />
            {label}
        </div>
    );
}

/* ─────────── Summary strip + progress ─────────── */

function SummaryStrip({ byTooth }) {
    const keys = Object.keys(byTooth).map(Number).filter((n) => authorState(byTooth[n]) !== "empty");
    const n = keys.length;
    const total = ALL_TEETH.length;
    if (n === 0) {
        return (
            <div
                className="flex items-center gap-2 px-3.5 py-2.5 bg-emerald-50 border-[0.5px] border-emerald-300 rounded-[8px] mb-4 text-[12px] text-emerald-900"
                data-testid="treatment-summary-strip"
            >
                <Info size={14} weight="duotone" />
                <span>No movements authored yet. Click a tooth to begin.</span>
            </div>
        );
    }
    const pct = Math.round((n / total) * 100);
    const sample = keys.slice(0, 4).map((k) => {
        const m = byTooth[k];
        const dirs = (m.dirs || []).slice(0, 2).join("+");
        return (
            <span key={k}>
                <strong className="font-bold">{k}</strong>: {dirs}
            </span>
        );
    });
    return (
        <div
            className="flex items-center gap-2 px-3.5 py-2.5 bg-emerald-50 border-[0.5px] border-emerald-300 rounded-[8px] mb-4 text-[12px] text-emerald-900 flex-wrap"
            data-testid="treatment-summary-strip"
        >
            <Info size={14} weight="duotone" />
            <span>
                <strong className="font-bold">{n} / {total}</strong> teeth authored ({pct}%)
            </span>
            {sample.map((el, i) => (
                <React.Fragment key={i}>
                    <span className="text-emerald-400">·</span>
                    {el}
                </React.Fragment>
            ))}
            {keys.length > 4 && (
                <>
                    <span className="text-emerald-400">·</span>
                    <span>+{keys.length - 4} more</span>
                </>
            )}
        </div>
    );
}

function ArchProgress({ byTooth }) {
    const uDone = UPPER_TEETH.filter((n) => (byTooth[n]?.dirs || []).length > 0).length;
    const lDone = LOWER_TEETH.filter((n) => (byTooth[n]?.dirs || []).length > 0).length;
    return (
        <div className="grid grid-cols-2 gap-2.5 mb-3.5" data-testid="treatment-arch-progress">
            <ProgRow label="Upper arch" done={uDone} total={16} testid="treatment-prog-upper" />
            <ProgRow label="Lower arch" done={lDone} total={16} testid="treatment-prog-lower" />
        </div>
    );
}

function ProgRow({ label, done, total, testid }) {
    const pct = Math.round((done / total) * 100);
    return (
        <div className="flex items-center gap-2.5 text-[11px] text-muted-foreground">
            <span className="whitespace-nowrap min-w-[56px]">{label}</span>
            <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                <div
                    className="h-full bg-emerald-600 rounded-full transition-all duration-300"
                    style={{ width: `${pct}%` }}
                    data-testid={`${testid}-fill`}
                />
            </div>
            <span className="whitespace-nowrap min-w-[60px] text-right font-medium" data-testid={`${testid}-label`}>
                {done} / {total}
            </span>
        </div>
    );
}

/* ─────────── Movement editor (inline drawer) ─────────── */

function MovementEditor({
    tooth, draft, setDraft, onSaveAndNext, onClose, onClear, onNav, navState,
}) {
    const type = TOOTH_TYPE[tooth] || "";
    const hasAnyDir = (draft.dirs || []).length > 0;

    const toggleDir = (id) => {
        setDraft((d) => {
            const has = (d.dirs || []).includes(id);
            return { ...d, dirs: has ? d.dirs.filter((x) => x !== id) : [...(d.dirs || []), id] };
        });
    };

    const setTorque = (t) => {
        setDraft((d) => ({ ...d, torque: d.torque === t ? "" : t }));
    };

    const preview = useMemo(() => describeMovement(draft), [draft]);

    return (
        <div
            className="bg-card border-[1.5px] border-emerald-600 rounded-[16px] mt-4 shadow-lg shadow-emerald-600/10 overflow-hidden"
            data-testid="treatment-movement-editor"
        >
            {/* Header */}
            <div className="bg-emerald-50 border-b-[0.5px] border-emerald-300 px-4 py-3.5 flex items-center justify-between">
                <div className="flex flex-col gap-0.5">
                    <div className="text-[14px] font-bold text-emerald-900 flex items-center gap-1.5">
                        <ToothIcon size={15} weight="duotone" />
                        <span data-testid="treatment-editor-title">Tooth {tooth} — {type}</span>
                    </div>
                    <div className="text-[11px] text-emerald-700/85" data-testid="treatment-editor-sub">
                        {hasAnyDir ? "Editing existing movements" : "No movements authored yet for this tooth"}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => onNav(-1)}
                        disabled={!navState.hasPrev}
                        className="w-[30px] h-[30px] border-[0.5px] border-emerald-300 rounded-[6px] bg-card text-emerald-900 flex items-center justify-center hover:bg-emerald-600 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-card disabled:hover:text-emerald-900 transition-colors"
                        title="Previous tooth (←)"
                        data-testid="treatment-editor-prev"
                    >
                        <CaretLeft size={14} weight="bold" />
                    </button>
                    <button
                        type="button"
                        onClick={() => onNav(1)}
                        disabled={!navState.hasNext}
                        className="w-[30px] h-[30px] border-[0.5px] border-emerald-300 rounded-[6px] bg-card text-emerald-900 flex items-center justify-center hover:bg-emerald-600 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-card disabled:hover:text-emerald-900 transition-colors"
                        title="Next tooth (→)"
                        data-testid="treatment-editor-next"
                    >
                        <CaretRight size={14} weight="bold" />
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-[12px] px-3 py-1.5 border-[0.5px] border-border rounded-[6px] text-muted-foreground hover:bg-muted/60 transition-colors flex items-center gap-1"
                        data-testid="treatment-editor-close"
                    >
                        <XCircle size={11} weight="bold" /> Close
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="p-4">
                {/* Direction section */}
                <div className="text-[10px] font-bold uppercase tracking-[0.06em] text-muted-foreground/70 mb-2">
                    Translation
                </div>
                <div className="grid grid-cols-4 gap-2 mb-4">
                    {DIRECTIONS.map((d) => {
                        const Icon = d.icon;
                        const on = (draft.dirs || []).includes(d.id);
                        return (
                            <button
                                key={d.id}
                                type="button"
                                onClick={() => toggleDir(d.id)}
                                data-testid={`treatment-dir-${d.id}`}
                                aria-pressed={on}
                                className={[
                                    "flex flex-col items-center justify-center gap-1 px-1.5 py-2.5 rounded-[8px] border-[1.5px] text-[11px] font-medium cursor-pointer select-none transition-colors text-center",
                                    on
                                        ? "bg-emerald-50 border-emerald-600 text-emerald-900 font-semibold"
                                        : "bg-card border-border text-muted-foreground hover:bg-emerald-50 hover:border-emerald-600 hover:text-emerald-900",
                                ].join(" ")}
                            >
                                <Icon
                                    size={18}
                                    weight={on ? "fill" : "regular"}
                                    className={on ? "text-emerald-600" : "text-muted-foreground/60"}
                                />
                                <span>{d.label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Metrics grid */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mb-4">
                    {/* Amount */}
                    <div className="border-[0.5px] border-border rounded-[8px] p-3 bg-muted/40">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground mb-2 flex items-center gap-1.5">
                            <Ruler size={13} weight="duotone" /> Amount
                        </div>
                        <Stepper
                            value={draft.amount}
                            onChange={(v) => setDraft((d) => ({ ...d, amount: String(v) }))}
                            step={0.5}
                            min={0}
                            max={15}
                            unit="mm"
                            testid="treatment-amount"
                            footnote="Leave blank if direction only"
                        />
                    </div>
                    {/* Rotation */}
                    <div className="border-[0.5px] border-border rounded-[8px] p-3 bg-muted/40">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground mb-2 flex items-center gap-1.5">
                            <ArrowsClockwise size={13} weight="duotone" /> Rotation
                        </div>
                        <Stepper
                            value={draft.rotation}
                            onChange={(v) => setDraft((d) => ({ ...d, rotation: String(v) }))}
                            step={1}
                            min={0}
                            max={90}
                            unit="°"
                            testid="treatment-rotation"
                            footnote="Degrees of rotation"
                        />
                    </div>
                    {/* Torque chips */}
                    <div className="border-[0.5px] border-border rounded-[8px] p-3 bg-muted/40">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground mb-2 flex items-center gap-1.5">
                            <ArrowsHorizontal size={13} weight="duotone" /> Torque
                        </div>
                        <div className="flex gap-1.5">
                            {[
                                { id: "positive", label: "+ Positive" },
                                { id: "signed", label: "Signed" },
                                { id: "negative", label: "− Negative" },
                            ].map((t) => (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setTorque(t.id)}
                                    className={[
                                        "flex-1 py-1.5 px-1 text-[11px] font-medium border-[0.5px] rounded-[6px] cursor-pointer text-center transition-colors",
                                        draft.torque === t.id
                                            ? "bg-emerald-50 border-emerald-600 text-emerald-900 font-semibold"
                                            : "bg-card border-border text-muted-foreground hover:border-emerald-600 hover:text-emerald-700",
                                    ].join(" ")}
                                    data-testid={`treatment-torque-${t.id}`}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1.5">Root torque direction</div>
                    </div>
                </div>

                {/* Anchorage + stage */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-4">
                    <div>
                        <Label className="text-[12px] font-medium text-muted-foreground mb-1.5 block">Anchorage</Label>
                        <select
                            value={draft.anchorage}
                            onChange={(e) => setDraft((d) => ({ ...d, anchorage: e.target.value }))}
                            className="w-full h-[36px] border-[0.5px] border-border rounded-[6px] px-2.5 text-[13px] bg-card outline-none focus:border-emerald-600"
                            data-testid="treatment-anchorage"
                        >
                            <option value="">Not specified</option>
                            {ANCHORAGE_OPTS.map((o) => (
                                <option key={o.v} value={o.v}>{o.l}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label className="text-[12px] font-medium text-muted-foreground mb-1.5 block">Stage onset</Label>
                        <Stepper
                            value={draft.stage}
                            onChange={(v) => setDraft((d) => ({ ...d, stage: String(v) }))}
                            step={1}
                            min={1}
                            max={60}
                            unit="stage"
                            placeholder="1"
                            testid="treatment-stage"
                        />
                    </div>
                </div>

                {/* Notes */}
                <div className="mb-4">
                    <Label className="text-[12px] font-medium text-muted-foreground mb-1.5 block">
                        Notes for this tooth <span className="text-muted-foreground/70 text-[11px]">(optional)</span>
                    </Label>
                    <textarea
                        value={draft.notes}
                        onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                        placeholder="e.g. Close space mesial first, then derotate. Prioritise this tooth in early stages."
                        className="w-full border-[0.5px] border-border rounded-[6px] px-2.5 py-2 text-[13px] bg-card outline-none focus:border-emerald-600 resize-y min-h-[64px] leading-snug"
                        data-testid="treatment-notes"
                    />
                </div>

                {/* Live preview */}
                <div className="text-[10px] font-bold uppercase tracking-[0.05em] text-muted-foreground/70 mb-1">
                    Movement summary preview
                </div>
                <div
                    className="bg-muted/40 border-[0.5px] border-border rounded-[8px] px-3.5 py-2.5 text-[12px] text-muted-foreground leading-relaxed min-h-[38px]"
                    data-testid="treatment-live-preview"
                >
                    {preview ? (
                        <>
                            <span>Tooth {tooth}: </span>
                            <strong className="text-emerald-900 font-semibold">{preview}</strong>
                            {draft.notes && (
                                <>
                                    <br />
                                    <span className="italic text-muted-foreground/85">&ldquo;{draft.notes}&rdquo;</span>
                                </>
                            )}
                        </>
                    ) : (
                        <span className="italic text-muted-foreground/70">Select directions above to see a summary.</span>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="border-t-[0.5px] border-border bg-muted/40 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onSaveAndNext}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-[6px] px-4 py-2 text-[13px] font-semibold flex items-center gap-1.5 transition-colors"
                        data-testid="treatment-save-next"
                    >
                        <CheckCircle size={14} weight="fill" />
                        Save &amp; Next
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="bg-transparent text-muted-foreground border-[0.5px] border-border rounded-[6px] px-3.5 py-2 text-[13px] hover:bg-muted/60 transition-colors"
                        data-testid="treatment-done"
                    >
                        Done
                    </button>
                </div>
                {hasAnyDir && (
                    <button
                        type="button"
                        onClick={onClear}
                        className="bg-transparent text-rose-600 border-[0.5px] border-rose-200 rounded-[6px] px-3 py-2 text-[12px] hover:bg-rose-50 transition-colors flex items-center gap-1"
                        data-testid="treatment-clear-tooth"
                    >
                        <Trash size={12} weight="duotone" />
                        Clear tooth
                    </button>
                )}
            </div>
        </div>
    );
}

/* ─────────── Main component ─────────── */

export default function TreatmentObjectives({
    rx,
    setRx,
    metadata,
    showToothChart = true,
}) {
    const objectives = rx.objectives || [];
    const rawMovements = rx.tooth_movements_plan || [];

    /** UI-shape map keyed by tooth number (integer) */
    const byTooth = useMemo(() => {
        const map = {};
        rawMovements.forEach((m) => {
            const ui = hydrate(m);
            if (ui) map[Number(ui.tooth)] = ui;
        });
        return map;
    }, [rawMovements]);

    const [activeTooth, setActiveTooth] = useState(null);
    const [draft, setDraft] = useState(null);

    /** Persist a draft → rx.tooth_movements_plan */
    const writeDraft = useCallback((toothNum, uiDraft) => {
        const next = serialise({ ...uiDraft, tooth: toothNum });
        const others = rawMovements.filter((m) => String(m.tooth) !== String(toothNum));
        const hasAny = (uiDraft.dirs || []).length > 0 || uiDraft.amount || uiDraft.rotation || uiDraft.notes;
        if (hasAny) {
            setRx((p) => ({ ...p, tooth_movements_plan: [...others, next] }));
        } else {
            // Empty draft — drop the record entirely
            setRx((p) => ({ ...p, tooth_movements_plan: others }));
        }
    }, [rawMovements, setRx]);

    const openTooth = useCallback((toothNum) => {
        setActiveTooth(toothNum);
        const existing = byTooth[toothNum];
        setDraft(existing ? { ...existing } : emptyMovement(toothNum));
    }, [byTooth]);

    const closeEditor = useCallback(() => {
        setActiveTooth(null);
        setDraft(null);
    }, []);

    const navigate = useCallback(
        (dir) => {
            if (activeTooth == null || !draft) return;
            // Save current draft first (silent)
            writeDraft(activeTooth, draft);
            const idx = ALL_TEETH.indexOf(activeTooth);
            const next = idx + dir;
            if (next >= 0 && next < ALL_TEETH.length) {
                const nextTooth = ALL_TEETH[next];
                setActiveTooth(nextTooth);
                // Re-read from the fresh byTooth (closure may be stale, so
                // compute from rawMovements + our just-saved snapshot)
                const existing = byTooth[nextTooth];
                setDraft(existing ? { ...existing } : emptyMovement(nextTooth));
            }
        },
        [activeTooth, draft, byTooth, writeDraft]
    );

    const saveAndNext = useCallback(() => {
        if (activeTooth == null || !draft) return;
        writeDraft(activeTooth, draft);
        const idx = ALL_TEETH.indexOf(activeTooth);
        if (idx < ALL_TEETH.length - 1) {
            const next = ALL_TEETH[idx + 1];
            setActiveTooth(next);
            const existing = byTooth[next];
            setDraft(existing ? { ...existing } : emptyMovement(next));
        } else {
            closeEditor();
        }
    }, [activeTooth, draft, byTooth, writeDraft, closeEditor]);

    const clearTooth = useCallback(() => {
        if (activeTooth == null) return;
        const others = rawMovements.filter((m) => String(m.tooth) !== String(activeTooth));
        setRx((p) => ({ ...p, tooth_movements_plan: others }));
        setDraft(emptyMovement(activeTooth));
    }, [activeTooth, rawMovements, setRx]);

    const clearAll = useCallback(async () => {
        if (rawMovements.length === 0) return;
        if (!await confirmDialog({ title: "Clear all tooth movements? This cannot be undone.", confirmText: "Confirm", tone: "danger" })) return;
        setRx((p) => ({ ...p, tooth_movements_plan: [] }));
        closeEditor();
    }, [rawMovements.length, setRx, closeEditor]);

    const toggleObjective = useCallback(
        (obj) => {
            setRx((p) => ({
                ...p,
                objectives: p.objectives.includes(obj)
                    ? p.objectives.filter((x) => x !== obj)
                    : [...p.objectives, obj],
            }));
        },
        [setRx]
    );

    /* Keyboard ← / → navigation */
    useEffect(() => {
        if (activeTooth == null) return undefined;
        const handler = (e) => {
            // Ignore when an input/textarea/select is focused
            const tag = (e.target?.tagName || "").toUpperCase();
            if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
            if (e.key === "ArrowRight") {
                e.preventDefault();
                navigate(1);
            } else if (e.key === "ArrowLeft") {
                e.preventDefault();
                navigate(-1);
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [activeTooth, navigate]);

    /* Smooth-scroll the editor into view on open */
    useEffect(() => {
        if (activeTooth == null) return;
        const el = document.querySelector('[data-testid="treatment-movement-editor"]');
        if (el && typeof el.scrollIntoView === "function") {
            el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }, [activeTooth]);

    /* When draft changes via setDraft we also need to mirror writes
       back to rx (debounced via direct write on each setter call would
       be too chatty; the simplest correct path is to call writeDraft
       only on Save & Next / navigate / close). For "live" feel, we
       additionally persist on draft autosave: */
    useEffect(() => {
        if (activeTooth == null || !draft) return;
        writeDraft(activeTooth, draft);
    }, [draft, activeTooth, writeDraft]);

    const navState = useMemo(() => {
        if (activeTooth == null) return { hasPrev: false, hasNext: false };
        const idx = ALL_TEETH.indexOf(activeTooth);
        return { hasPrev: idx > 0, hasNext: idx < ALL_TEETH.length - 1 };
    }, [activeTooth]);

    return (
        <div className="space-y-5" data-testid="treatment-objectives-block">
            {/* ═══════════ TREATMENT OBJECTIVES ═══════════ */}
            <div className="bg-card border-[0.5px] border-border rounded-[16px] p-5">
                <SectionHeader
                    title="Treatment Objectives"
                    sub="select every goal that applies — drives planner intent and lab specs"
                    right={
                        <span
                            className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-900 border-[0.5px] border-emerald-600"
                            data-testid="treatment-objectives-counter"
                        >
                            {objectives.length} selected
                        </span>
                    }
                />

                <div className="flex flex-wrap mt-3" data-testid="treatment-objectives-grid">
                    {(metadata?.objectives || []).map((obj) => {
                        const on = objectives.includes(obj);
                        const label = obj
                            .split("_")
                            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                            .join(" ");
                        const Icon = OBJECTIVE_ICON[obj] || Smiley;
                        return (
                            <button
                                key={obj}
                                type="button"
                                onClick={() => toggleObjective(obj)}
                                aria-pressed={on}
                                data-testid={`treatment-obj-${obj}`}
                                className={[
                                    "inline-flex items-center gap-1.5 text-[13px] font-medium px-3.5 py-2 rounded-full border-[1.5px] m-1 cursor-pointer select-none transition-all",
                                    on
                                        ? "bg-emerald-50 border-emerald-600 text-emerald-900 font-semibold"
                                        : "bg-card border-border text-muted-foreground hover:border-emerald-600 hover:text-emerald-900 hover:bg-emerald-50",
                                ].join(" ")}
                            >
                                <Icon
                                    size={15}
                                    weight={on ? "fill" : "regular"}
                                    className={on ? "text-emerald-600" : "text-muted-foreground/70"}
                                />
                                <span>{label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ═══════════ TOOTH-BY-TOOTH PLAN ═══════════ */}
            {showToothChart && (
                <div className="bg-card border-[0.5px] border-border rounded-[16px] p-5">
                    <SectionHeader
                        title="Tooth-by-Tooth Movement Plan"
                        right={
                            rawMovements.length > 0 && (
                                <button
                                    type="button"
                                    onClick={clearAll}
                                    className="text-[12px] text-muted-foreground border-[0.5px] border-border rounded-[6px] px-3 py-1.5 hover:bg-muted/60 flex items-center gap-1.5 transition-colors"
                                    data-testid="treatment-clear-all"
                                >
                                    <ArrowsClockwise size={13} weight="duotone" /> Clear all
                                </button>
                            )
                        }
                    />
                    <p className="text-[12px] text-muted-foreground mb-4 pl-3 leading-snug">
                        Click a tooth to author its movements. The lab manufactures aligners per this spec — be specific.
                    </p>

                    <Legend />
                    <SummaryStrip byTooth={byTooth} />
                    <ArchProgress byTooth={byTooth} />

                    <ToothChart byTooth={byTooth} activeTooth={activeTooth} onTap={openTooth} />

                    {activeTooth != null && draft && (
                        <MovementEditor
                            tooth={activeTooth}
                            draft={draft}
                            setDraft={setDraft}
                            onSaveAndNext={saveAndNext}
                            onClose={closeEditor}
                            onClear={clearTooth}
                            onNav={navigate}
                            navState={navState}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
